// SK: how long the "Item added" panel stays up before it takes itself away.
// The design review asked for it to dismiss on its own after a few seconds so
// the cover is never left sitting behind a box. The timer pauses while the
// pointer is over the panel and is cancelled outright once focus moves onto
// one of its controls, so it can never pull the panel away from someone who
// is reading it or tabbing through it.
const SK_CART_NOTIFICATION_DISMISS_MS = 8000;

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.dismissTimer = null;
    this.autoDismissDisabled = false;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this)),
    );

    this.notification.addEventListener('mouseenter', () => this.cancelAutoDismiss());
    this.notification.addEventListener('mouseleave', () => this.scheduleAutoDismiss());
    // trapFocus() focuses the dialog itself on open, which fires focusin with
    // the panel as the target. Only focus landing on something *inside* the
    // panel means a person is actually working it.
    this.notification.addEventListener('focusin', (evt) => {
      if (evt.target !== this.notification) this.cancelAutoDismiss(true);
    });
  }

  scheduleAutoDismiss() {
    if (this.autoDismissDisabled) return;
    this.cancelAutoDismiss();
    this.dismissTimer = setTimeout(() => this.close(), SK_CART_NOTIFICATION_DISMISS_MS);
  }

  cancelAutoDismiss(permanently = false) {
    if (permanently) this.autoDismissDisabled = true;
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.scheduleAutoDismiss();

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        }),
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.cancelAutoDismiss();
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    // A fresh add is a fresh panel: it gets its own countdown even if an
    // earlier one was cancelled by focus.
    this.autoDismissDisabled = false;
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
